/**
 * OptionPageSettings Component
 *
 * Renders a settings panel for configuring a WordPress options page.
 * Used within the VoxyCure Framework to define menu and page properties for admin settings.
 *
 * @param {Object}   props
 * @param {Object}   props.pageData - Current options page data state.
 * @param {Function} props.setPageData - Function to update options page data.
 *
 * @returns {JSX.Element}
 */

import { TextControl, SelectControl } from '@wordpress/components';
import { fieldComponents } from '../../components/fieldComponents';
import { __ } from '@wordpress/i18n';

const OptionPageSettings = ({ pageData, setPageData }) => {

    const handleSectionsChange = (tabs) => {
        setPageData(prev => ({ ...prev, tabs }));
    };

    // Auto-generate slug from name if not already set.
    const handleNameBlur = () => {
        if (!pageData.slug && pageData.name) {
            const slug = pageData.name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)+/g, '');
            setPageData(prev => ({ ...prev, slug }));
        }
    };

    const capabilityOptions = [
        { label: 'manage_options', value: 'manage_options' },
        { label: 'edit_posts', value: 'edit_posts' },
        { label: 'publish_posts', value: 'publish_posts' },
        { label: 'edit_theme_options', value: 'edit_theme_options' },
        { label: 'administrator', value: 'administrator' },
    ];

    const tabAlignmentOptions = [
        { label: __('Horizontal', 'voxycure-framework'), value: 'horizontal' },
        { label: __('Vertical', 'voxycure-framework'), value: 'vertical' },
    ];

    const FieldOption = fieldComponents['options']; // Field option.

    return (
        <>
            <TextControl
                label={__('Menu Name', 'voxycure-framework')}
                value={pageData.name}
                onChange={(value) => setPageData({ ...pageData, name: value })}
                onBlur={handleNameBlur}
                required
            />

            <TextControl
                label={__('Menu Slug', 'voxycure-framework')}
                value={pageData.slug || ''}
                onChange={(value) => setPageData({ ...pageData, slug: value })}
                help={__('Unique identifier used in admin menu URL.', 'voxycure-framework')}
                required
            />

            <TextControl
                label={__('Option Key', 'voxycure-framework')}
                value={pageData.option_key || ''}
                onChange={(value) => setPageData({ ...pageData, option_key: value })}
                help={__('The unique key used to store settings in the options table.', 'voxycure-framework')}
            />

            <TextControl
                label={__('Page Title', 'voxycure-framework')}
                value={pageData.page_title || ''}
                onChange={(value) => setPageData({ ...pageData, page_title: value })}
            />

            <TextControl
                label={__('Icon (Dashicon class or custom SVG)', 'voxycure-framework')}
                value={pageData.icon}
                onChange={(val) => setPageData(prev => ({ ...prev, icon: val }))}
            />

            <TextControl
                label={__('Parent Slug (optional)', 'voxycure-framework')}
                value={pageData.parent_slug || ''}
                onChange={(value) => setPageData({ ...pageData, parent_slug: value })}
                help={__('Leave blank to show in top-level menu.', 'voxycure-framework')}
            />

            <TextControl
                label={__('Menu Position', 'voxycure-framework')}
                type="number"
                value={pageData.position || ''}
                onChange={(value) =>
                    setPageData({ ...pageData, position: parseInt(value) || '' })
                }
                min={1}
                help={__('Optional. Defines where the menu item appears in admin.', 'voxycure-framework')}
            />

            <SelectControl
                label={__('Capability', 'voxycure-framework')}
                value={pageData.capability}
                options={capabilityOptions}
                onChange={(val) => setPageData(prev => ({ ...prev, capability: val }))}
            />

            <SelectControl
                label={__('Tab Alignment', 'voxycure-framework')}
                value={pageData.tab_alignment || 'horizontal'}
                options={tabAlignmentOptions}
                onChange={(val) => setPageData(prev => ({ ...prev, tab_alignment: val }))}
                help={__('Choose whether tabs are displayed horizontally or vertically.', 'voxycure-framework')}
            />

            <FieldOption
                options={pageData.tabs || []}
                onChange={(updatedTabs) => {
                    setPageData(prev => {
                        const newFields = prev.fields.map(field => ({
                            ...field,
                            tab: updatedTabs.length === 0 ? '' : field.tab // reset if no tabs
                        }));
                        return { ...prev, tabs: updatedTabs, fields: newFields };
                    });
                }}
            />

        </>
    );
};

export default OptionPageSettings;
